// ===================================
// KAIXO - LatidoCarousel
// Visor de carrusel (foto + video corto) para publicaciones de canal.
// Independiente del modal principal: se monta donde se renderice un post.
// Usa CSS scroll-snap para swipe nativo sin librerías.
// ===================================

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { optimizedVideoUrl, optimizedImageUrl } from '../../utils/cloudinaryUrl';
import './LatidoCarousel.css';

// Umbral de desplazamiento para distinguir tap vs swipe. Debajo de 8px el
// gesto se considera tap (abre fullscreen); encima se considera swipe
// horizontal para cambiar de slide.
const TAP_THRESHOLD_PX = 8;

/**
 * @param {Array<{url: string, type: 'image'|'video'}>} media
 *   Array del carrusel. Si el post es antiguo (solo mediaUrl), el padre
 *   debe pasar [{url: mediaUrl, type: mediaType}].
 * @param {(url: string) => void} [onOpenImage]
 *   Callback opcional cuando se toca una imagen (para abrir ImageViewer).
 */
export default function LatidoCarousel({ media = [], onOpenImage, onOpenVideo }) {
    const [index, setIndex] = useState(0);
    // Modo fullscreen: el propio carrusel ocupa 100% viewport con swipe
    // entre slides. Así el usuario puede navegar todas las fotos/videos
    // sin salir del visor (Instagram-like).
    const [fullscreen, setFullscreen] = useState(false);
    const trackRef = useRef(null);
    const videosRef = useRef([]);
    // Pointer-start para diferenciar tap vs swipe. En scroll-snap, los
    // onClick a veces son "comidos" por el scroll; nosotros medimos el
    // desplazamiento y emitimos el fullscreen a mano si se quedó corto.
    const pointerStartRef = useRef(null);

    // Pausar videos al cambiar de slide para evitar solapes de audio.
    useEffect(() => {
        videosRef.current.forEach((v, i) => {
            if (!v) return;
            if (i === index) {
                // Autoplay del video actual si es el de delante (silenciado
                // para cumplir políticas de autoplay en Android/WebView).
                v.play().catch(() => {});
            } else {
                v.pause();
                try { v.currentTime = 0; } catch {}
            }
        });
    }, [index]);

    // Sincroniza el índice cuando el usuario hace swipe manual.
    const onScroll = () => {
        const el = trackRef.current;
        if (!el) return;
        const w = el.clientWidth;
        if (w <= 0) return;
        const i = Math.round(el.scrollLeft / w);
        if (i !== index && i >= 0 && i < media.length) setIndex(i);
    };

    const goTo = (i) => {
        const el = trackRef.current;
        if (!el) return;
        const w = el.clientWidth;
        el.scrollTo({ left: i * w, behavior: 'smooth' });
    };

    // Al entrar/salir de fullscreen el ancho del track cambia → hay que
    // hacer scrollTo del slide actual para mantenerlo visible. Sin esto
    // el track se queda en scrollLeft=0 cuando cambia el ancho.
    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;
        // Timeout para esperar al repaint del layout nuevo.
        const t = setTimeout(() => {
            el.scrollLeft = index * el.clientWidth;
        }, 50);
        return () => clearTimeout(t);
    }, [fullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

    // ESC cierra el fullscreen
    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    if (!media.length) return null;
    const single = media.length === 1;

    const inner = (
        <div className={`lc-wrap ${single ? 'lc-single' : ''} ${fullscreen ? 'lc-fullscreen' : ''}`}>
            {fullscreen && (
                <button
                    className="lc-close-fs"
                    onClick={() => setFullscreen(false)}
                    aria-label="Cerrar pantalla completa"
                >✕</button>
            )}
            <div
                ref={trackRef}
                className="lc-track"
                onScroll={onScroll}
            >
                {media.map((m, i) => (
                    <div
                        key={i}
                        className="lc-slide"
                        onPointerDown={(e) => {
                            pointerStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
                        }}
                        onPointerUp={(e) => {
                            const start = pointerStartRef.current;
                            pointerStartRef.current = null;
                            if (!start) return;
                            const dx = Math.abs(e.clientX - start.x);
                            const dy = Math.abs(e.clientY - start.y);
                            const dt = Date.now() - start.t;
                            // Fue tap: poco desplazamiento y duración razonable
                            if (dx < TAP_THRESHOLD_PX && dy < TAP_THRESHOLD_PX && dt < 500) {
                                // Si es un carrusel (≥2 medios), entramos en modo
                                // fullscreen interno para poder hacer swipe entre
                                // todos los slides en pantalla completa.
                                // Si es un medio único, usamos el viewer externo.
                                if (media.length >= 2) {
                                    setFullscreen(true);
                                } else if (m.type === 'video') {
                                    if (onOpenVideo) onOpenVideo(m.url);
                                } else if (onOpenImage) {
                                    onOpenImage(m.url);
                                }
                            }
                        }}
                    >
                        {/* Capa de fondo blur: misma media ampliada y borrosa,
                            rellena los huecos cuando el vídeo/imagen no llena
                            el aspect-ratio del slide. Para vídeo, un <video>
                            muteado en preload="metadata" muestra el primer
                            frame con blur — sin reproducir, solo decoración.
                            Para imagen, la propia <img>. */}
                        {m.type === 'video' ? (
                            <video
                                // #t=0.001 fuerza al WebView (iOS Safari sobre
                                // todo) a renderizar el primer frame en lugar
                                // de quedar en negro hasta interacción.
                                src={optimizedVideoUrl(m.url) + '#t=0.001'}
                                className="lc-media-bg"
                                aria-hidden="true"
                                muted
                                playsInline
                                preload="metadata"
                                tabIndex={-1}
                            />
                        ) : (
                            <img
                                src={optimizedImageUrl(m.url)}
                                alt=""
                                aria-hidden="true"
                                loading="lazy"
                                decoding="async"
                                className="lc-media-bg"
                            />
                        )}
                        {m.type === 'video' ? (
                            <video
                                ref={el => (videosRef.current[i] = el)}
                                src={optimizedVideoUrl(m.url)}
                                className="lc-media"
                                playsInline
                                muted
                                loop
                                preload={i === index ? 'auto' : 'metadata'}
                            />
                        ) : (
                            <img
                                src={optimizedImageUrl(m.url)}
                                alt=""
                                loading={i === 0 ? 'eager' : 'lazy'}
                                decoding="async"
                                className="lc-media"
                            />
                        )}
                    </div>
                ))}
            </div>

            {!single && (
                <>
                    {/* Flechas (escritorio). En móvil se usa swipe. */}
                    {index > 0 && (
                        <button
                            className="lc-arrow lc-arrow-left"
                            onClick={() => goTo(index - 1)}
                            aria-label="Anterior"
                        >‹</button>
                    )}
                    {index < media.length - 1 && (
                        <button
                            className="lc-arrow lc-arrow-right"
                            onClick={() => goTo(index + 1)}
                            aria-label="Siguiente"
                        >›</button>
                    )}

                    {/* Indicador de páginas (bullets) */}
                    <div className="lc-dots">
                        {media.map((_, i) => (
                            <span
                                key={i}
                                className={`lc-dot ${i === index ? 'lc-dot-active' : ''}`}
                            />
                        ))}
                    </div>

                    {/* Contador "2 / 5" */}
                    <div className="lc-counter">{index + 1} / {media.length}</div>
                </>
            )}
        </div>
    );

    // En fullscreen montamos vía portal para evitar overflow:hidden de
    // ancestros (feed scroll) que recortaría el visor.
    return fullscreen ? ReactDOM.createPortal(inner, document.body) : inner;
}
